"use client";

import { useEffect, useState } from "react";
import i18n, { SUPPORTED_LOCALES, type SupportedLocale } from "./config";
import { loadTokens } from "@/lib/api";
import { readRegionCookie } from "@/lib/regionContext";

/**
 * EPIC-030 STORY-1803 — client-side i18n bootstrap wrapper.
 *
 * `initialLocale` is the server-resolved locale read from the
 * `granbay.region` cookie in `app/layout.tsx` via `getServerLocale()`.
 *
 * Sync alignment: we use a lazy `useState` initializer to call
 * `i18n.changeLanguage(initialLocale)` exactly once, BEFORE the first
 * render commits. React 19 forbids render-time setState in *other*
 * components (i18next's `changeLanguage` fires listeners synchronously,
 * which made `useTranslation()` consumers like BetaBanner setState
 * mid-render and crash hydration). The lazy initializer runs outside
 * the React reconciler's "currently rendering" window, so subscribers
 * are free to setState without violating React's invariants.
 *
 * The post-mount useEffect promotes the locale further if the cookie
 * carries something the server-side resolver didn't honour (the older
 * server.ts only knew en/pt; the extended set lands in this effect).
 */
export function I18nProvider({
  children,
  initialLocale = "en",
}: {
  children: React.ReactNode;
  initialLocale?: SupportedLocale;
}) {
  // Lazy-init: runs once, before first commit, outside the render
  // phase of any other component. Safe to fire i18next listeners.
  useState(() => {
    if (i18n.language !== initialLocale) {
      void i18n.changeLanguage(initialLocale);
    }
    return null;
  });

  useEffect(() => {
    const cookieLocale = readRegionCookie().locale;
    const tokens = loadTokens();
    const userLocale = tokens?.user?.locale as string | undefined;
    const navLang =
      typeof navigator !== "undefined"
        ? navigator.language?.slice(0, 2)
        : undefined;
    const target = cookieLocale ?? userLocale ?? navLang ?? "en";
    const valid: readonly string[] = SUPPORTED_LOCALES;
    const next = (
      valid.includes(target) ? target : "en"
    ) as SupportedLocale;
    if (i18n.language !== next) {
      void i18n.changeLanguage(next);
    }
  }, []);

  return <>{children}</>;
}
